// Display URL card: shows {origin}{displayPath} with a copy affordance, and
// a "Regenerate URL" action (destructive — it invalidates the current URL for every
// player already showing it) behind kore's type-to-confirm DestructiveConfirm
// -> POST /v1/screens/{id}/token/rotate. The rotate mutation's own
// `onSuccess` invalidates the screen-detail query, but this card also shows
// the fresh path straight from the mutation's response so the UI updates
// immediately rather than waiting on that refetch (same reasoning as
// stream-form.tsx's PATCH-response reset).

import { Button, Card, CopyButton, DestructiveConfirm, toast } from "@pej/kore/components";
import { useState } from "react";
import { useRotateToken } from "@/api/screens";
import type { Screen } from "@/api/types";
import { toastMutationError } from "@/components/common/toast-mutation-error";

export interface DisplayUrlCardProps {
    screen: Screen;
}

export function DisplayUrlCard({ screen }: DisplayUrlCardProps) {
    const rotateToken = useRotateToken(screen.locationId, screen.id);
    const [displayPath, setDisplayPath] = useState(screen.displayPath);
    const [confirmOpen, setConfirmOpen] = useState(false);

    // The display page (/d/…) is served by the UI host — the admin's own
    // origin — not env.apiBaseUrl, which points at the separate API host.
    const fullUrl = `${window.location.origin}${displayPath}`;

    async function handleRotate() {
        try {
            const result = await rotateToken.mutateAsync();
            setDisplayPath(result.displayPath);
            toast.success("New display URL generated");
            setConfirmOpen(false);
        } catch (err) {
            toastMutationError("Regenerate failed", err);
        }
    }

    return (
        <Card className="p-6">
            <h2 className="mb-4 text-base font-semibold">Display URL</h2>
            <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded-kore-md border border-kore-border-soft bg-kore-bg px-3 py-2 text-sm">
                    {fullUrl}
                </code>
                <CopyButton value={fullUrl} label="Copy display URL" />
            </div>
            <p className="mt-2 text-sm text-kore-text-muted">
                Open this URL on the screen that should show this waiting time.
            </p>
            <div className="mt-4 flex justify-end">
                <Button variant="outline" onClick={() => setConfirmOpen(true)}>
                    Regenerate URL
                </Button>
            </div>
            <DestructiveConfirm
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                title="Regenerate the display URL"
                description="The old URL stops working at once. Any screen still on it goes dark until the new URL is opened there."
                expectedConfirmation={screen.name}
                echoLabel="Type the screen's name to confirm"
                primaryLabel="Regenerate"
                confirming={rotateToken.isPending}
                onConfirm={handleRotate}
            />
        </Card>
    );
}
